import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationTestProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const PaginationTest: React.FC<PaginationTestProps> = ({ currentPage, totalPages, onPageChange }) => {
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // غیرفعال کردن لودر بعد از دریافت داده جدید
    setLoading(false);
  }, [currentPage]);

  const handlePageChange = (page: number) => {
    if (page !== currentPage) {
      setLoading(true);
      onPageChange(page);
    }
  };

  const renderPages = () => {
    const pages: JSX.Element[] = [];
    pages.push(
      <li key={0}>
        <Link
          to="#!"
          className={`inline-flex items-center justify-center bg-white dark:bg-zinc-700 h-8 w-8 transition-all duration-150 ease-linear border rounded border-slate-200 dark:border-zinc-500 text-slate-500 dark:text-zinc-200 hover:text-custom-500 dark:hover:text-custom-500 hover:bg-custom-100 dark:hover:bg-custom-500/10 focus:bg-custom-50 dark:focus:bg-custom-500/10 focus:text-custom-500 dark:focus:text-custom-500 ${
            currentPage === 1 && 'bg-custom-100 text-custom-500 dark:bg-custom-500/10'
          }`}
          onClick={() => handlePageChange(1)}
        >
          1
        </Link>
      </li>
    );

    if (currentPage > 3) {
      pages.push(<li key="start-dots" className="text-slate-500">...</li>);
    }

    const startPage = Math.max(2, currentPage - 1);
    const endPage = Math.min(totalPages - 1, currentPage + 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i}>
          <Link
            to="#!"
            className={`inline-flex items-center justify-center bg-white dark:bg-zinc-700 h-8 w-8 transition-all duration-150 ease-linear border rounded border-slate-200 dark:border-zinc-500 text-slate-500 dark:text-zinc-200 hover:text-custom-500 dark:hover:text-custom-500 hover:bg-custom-100 dark:hover:bg-custom-500/10 focus:bg-custom-50 dark:focus:bg-custom-500/10 focus:text-custom-500 dark:focus:text-custom-500 ${
              currentPage === i && 'bg-custom-100 text-custom-500 dark:bg-custom-500/10'
            }`}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </Link>
        </li>
      );
    }

    if (currentPage < totalPages - 2) {
      pages.push(<li key="end-dots" className="text-slate-500">...</li>);
    }

    if (totalPages > 1) {
      pages.push(
        <li key={totalPages}>
          <Link
            to="#!"
            className={`inline-flex items-center justify-center bg-white dark:bg-zinc-700 h-8 w-8 transition-all duration-150 ease-linear border rounded border-slate-200 dark:border-zinc-500 text-slate-500 dark:text-zinc-200 hover:text-custom-500 dark:hover:text-custom-500 hover:bg-custom-100 dark:hover:bg-custom-500/10 focus:bg-custom-50 dark:focus:bg-custom-500/10 focus:text-custom-500 dark:focus:text-custom-500 ${
              currentPage === totalPages && 'bg-custom-100 text-custom-500 dark:bg-custom-500/10'
            }`}
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </Link>
        </li>
      );
    }

    return pages;
  };

  return (
    <div className="flex items-center flex-wrap mt-4 gap-2 relative">
      {/* لودر */}
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center dark:bg-opacity-50 dark:bg-gray-900 z-10">
          <div className="w-12 h-12 border-4 border-t-transparent border-blue-500 rounded-full animate-spin"></div>
        </div>
      )}

      {/* دکمه قبلی */}
      <button
        disabled={currentPage === 1}
        onClick={() => handlePageChange(currentPage - 1)}
        className="inline-flex items-center justify-center bg-white dark:bg-zinc-700 h-8 px-3 transition-all duration-150 ease-linear border rounded border-slate-200 dark:border-zinc-500 text-slate-500 dark:text-zinc-200 hover:text-custom-500 dark:hover:text-custom-500 hover:bg-custom-50 dark:hover:bg-custom-500/10 focus:bg-custom-50 dark:focus:bg-custom-500/10 focus:text-custom-500 dark:focus:text-custom-500"
      >
        <ChevronLeft className="size-4 mr-1 rtl:rotate-180" /> قبلی
      </button>

      {/* شماره صفحات */}
      <ul className="flex items-center flex-wrap gap-2">{renderPages()}</ul>

      {/* دکمه بعدی */}
      <button
        disabled={currentPage === totalPages}
        onClick={() => handlePageChange(currentPage + 1)}
        className="inline-flex items-center justify-center bg-white dark:bg-zinc-700 h-8 px-3 transition-all duration-150 ease-linear border rounded border-slate-200 dark:border-zinc-500 text-slate-500 dark:text-zinc-200 hover:text-custom-500 dark:hover:text-custom-500 hover:bg-custom-50 dark:hover:bg-custom-500/10 focus:bg-custom-50 dark:focus:bg-custom-500/10 focus:text-custom-500 dark:focus:text-custom-500"
      >
        بعدی <ChevronRight className="size-4 ml-1 rtl:rotate-180" />
      </button>
    </div>
  );
};

export default PaginationTest;
